import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * The dashboard scrolls inside this element, not the document, so the browser's own
 * scroll restoration never applies to it — see DashboardPage.
 */
export const SCROLL_CONTAINER_ID = 'page-scroll-container';

const storageKey = (pathname: string) => `scroll:${pathname}`;

/**
 * Remembers where a long page was scrolled to and puts the user back there when they
 * return to it — after opening a profile and pressing Back, for instance.
 *
 * @param ready Pass `true` once the content that makes the page tall has rendered.
 *              Restoring before that just scrolls an empty container back to 0.
 */
export function useScrollRestoration(ready: boolean) {
  const { pathname } = useLocation();
  const hasRestored = useRef(false);

  // Track the scroll position while the user reads.
  useEffect(() => {
    const el = document.getElementById(SCROLL_CONTAINER_ID);
    if (!el) return;

    let frame = 0;
    const save = () => sessionStorage.setItem(storageKey(pathname), String(el.scrollTop));
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        save();
      });
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
      save(); // the pending frame never runs when we unmount mid-navigation
    };
  }, [pathname]);

  // Restore once per visit, as soon as there is something to scroll.
  useEffect(() => {
    if (!ready || hasRestored.current) return;
    hasRestored.current = true;

    const saved = Number(sessionStorage.getItem(storageKey(pathname)));
    if (!saved) return;

    const applyScroll = () => {
      const container = document.getElementById(SCROLL_CONTAINER_ID);
      if (container) {
        container.scrollTop = saved;
      }
    };

    requestAnimationFrame(applyScroll);
    const t1 = setTimeout(applyScroll, 60);
    const t2 = setTimeout(applyScroll, 200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [ready, pathname]);
}
